import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import { ToolUseCaseAssignment } from '../../tools/components/ToolUseCaseAssignment';
import {
  createAiToolPlugin,
  deleteAiToolPlugin,
  getAiToolPluginCatalog,
  getAiToolPluginUseCaseAssignments,
  listAiToolPlugins,
  updateAiToolPlugin,
} from '../services/toolPluginService';
import type {
  AiToolPluginCatalogItem,
  AiToolPluginDefinition,
  AiToolPluginSource,
} from '../types/ToolPlugin';
import {
  emptyToolPluginForm,
  suggestKernelPluginName,
  validateToolPluginForm,
  type ToolPluginFormIssue,
  type ToolPluginFormState,
} from '../utils/toolPluginFormUtils';
import {
  AlertIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  LayersIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  ToolboxIcon,
  TrashIcon,
} from './ToolPluginIcons';
import styles from './ToolPluginsPage.module.css';

type StatusFilter = 'all' | 'published' | 'draft';
type SourceFilter = 'all' | AiToolPluginSource;
type DrawerMode = 'create' | 'edit' | null;

const toFormState = (plugin: AiToolPluginDefinition): ToolPluginFormState => ({
  pluginId: plugin.pluginId,
  kernelPluginName: plugin.kernelPluginName,
  displayName: plugin.displayName,
  description: plugin.description,
  isPublished: plugin.isPublished,
  memberFunctionIds: [...plugin.memberFunctionIds],
  assignedUseCaseIds: [],
});

export const ToolPluginsPage: React.FC = () => {
  const { t } = useTranslation(['toolPlugins', 'common']);
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManage = hasPermission(PermissionScopes.aiToolPluginsManage);

  const [loading, setLoading] = useState(true);
  const [plugins, setPlugins] = useState<AiToolPluginDefinition[]>([]);
  const [catalog, setCatalog] = useState<AiToolPluginCatalogItem[]>([]);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
  const [form, setForm] = useState<ToolPluginFormState>(emptyToolPluginForm);
  const [kernelNameTouched, setKernelNameTouched] = useState(false);
  const [formIssue, setFormIssue] = useState<ToolPluginFormIssue | null>(null);
  const [saving, setSaving] = useState(false);

  const [pickerQuery, setPickerQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [showUseCaseOptions, setShowUseCaseOptions] = useState(false);
  const [useCaseAssignmentsLoaded, setUseCaseAssignmentsLoaded] = useState(false);

  /* ------------------------------------------------------------ derived */

  const publishedPluginCount = useMemo(
    () => plugins.filter((plugin) => plugin.isPublished).length,
    [plugins],
  );

  const groupedToolCount = useMemo(
    () => catalog.filter((item) => item.memberOfPluginIds.length > 0).length,
    [catalog],
  );

  const pluginNamesById = useMemo(() => {
    const map = new Map<string, string>();
    plugins.forEach((plugin) => map.set(plugin.pluginId, plugin.displayName || plugin.kernelPluginName));
    return map;
  }, [plugins]);

  const catalogByFunctionId = useMemo(() => {
    const map = new Map<string, AiToolPluginCatalogItem>();
    catalog.forEach((item) => map.set(item.functionId, item));
    return map;
  }, [catalog]);

  const visiblePlugins = useMemo(() => {
    const search = query.trim().toLowerCase();

    return plugins.filter((plugin) => {
      if (statusFilter === 'published' && !plugin.isPublished) {
        return false;
      }

      if (statusFilter === 'draft' && plugin.isPublished) {
        return false;
      }

      if (!search) {
        return true;
      }

      return plugin.displayName.toLowerCase().includes(search)
        || plugin.kernelPluginName.toLowerCase().includes(search)
        || plugin.description.toLowerCase().includes(search);
    });
  }, [plugins, query, statusFilter]);

  const sortedCatalog = useMemo(() => {
    const bySource = (item: AiToolPluginCatalogItem) => (item.source === 'mcp' ? 0 : 1);
    return [...catalog].sort((left, right) => (
      bySource(left) - bySource(right)
      || left.displayName.localeCompare(right.displayName)
    ));
  }, [catalog]);

  const visibleCatalog = useMemo(() => {
    const search = pickerQuery.trim().toLowerCase();

    return sortedCatalog.filter((item) => {
      if (sourceFilter !== 'all' && item.source !== sourceFilter) {
        return false;
      }

      if (!search) {
        return true;
      }

      return item.displayName.toLowerCase().includes(search)
        || item.kernelFunctionName.toLowerCase().includes(search)
        || item.functionId.toLowerCase().includes(search);
    });
  }, [pickerQuery, sortedCatalog, sourceFilter]);

  const selectedMembers = useMemo(
    () => form.memberFunctionIds.map((functionId) => (
      catalogByFunctionId.get(functionId)
      ?? {
        functionId,
        displayName: functionId,
        kernelFunctionName: functionId,
        source: functionId.startsWith('mcp.') ? 'mcp' : 'api',
        isPublished: false,
        memberOfPluginIds: [],
      } as AiToolPluginCatalogItem
    )),
    [catalogByFunctionId, form.memberFunctionIds],
  );

  // Omitting assignedUseCaseIds leaves member scoping untouched, so an in-flight or failed load
  // must not submit the empty placeholder selection and unassign every member.
  const canEditUseCaseAssignments = drawerMode === 'create' || useCaseAssignmentsLoaded;

  /* -------------------------------------------------------------- loading */

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pluginRows, catalogRows] = await Promise.all([
        listAiToolPlugins(),
        getAiToolPluginCatalog(),
      ]);

      setPlugins(pluginRows);
      setCatalog(catalogRows);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (drawerMode !== 'edit' || !form.pluginId) {
      return undefined;
    }

    const pluginId = form.pluginId;
    let cancelled = false;
    setUseCaseAssignmentsLoaded(false);

    void getAiToolPluginUseCaseAssignments(pluginId)
      .then((assignedUseCaseIds) => {
        if (!cancelled) {
          setForm((current) => (
            current.pluginId === pluginId
              ? { ...current, assignedUseCaseIds }
              : current
          ));
          setUseCaseAssignmentsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setForm((current) => (
            current.pluginId === pluginId
              ? { ...current, assignedUseCaseIds: [] }
              : current
          ));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [drawerMode, form.pluginId]);

  useEffect(() => {
    if (!drawerMode) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDrawerMode(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [drawerMode]);

  /* ----------------------------------------------------------- drawer flow */

  const resetDrawerControls = (expandUseCaseOptions = false) => {
    setPickerQuery('');
    setSourceFilter('all');
    setShowUseCaseOptions(expandUseCaseOptions);
  };

  const openCreateDrawer = () => {
    setForm(emptyToolPluginForm);
    setKernelNameTouched(false);
    setFormIssue(null);
    // Grouping tools rescopes them to the default selection, so it stays visible rather than
    // folded away behind the disclosure.
    resetDrawerControls(true);
    setDrawerMode('create');
  };

  const openEditDrawer = (plugin: AiToolPluginDefinition) => {
    setForm(toFormState(plugin));
    setKernelNameTouched(true);
    setFormIssue(null);
    setUseCaseAssignmentsLoaded(false);
    resetDrawerControls();
    setDrawerMode('edit');
  };

  const handleDisplayNameChange = (displayName: string) => {
    setForm((current) => ({
      ...current,
      displayName,
      kernelPluginName: kernelNameTouched
        ? current.kernelPluginName
        : suggestKernelPluginName(displayName),
    }));
  };

  const toggleMember = (functionId: string) => {
    setForm((current) => ({
      ...current,
      memberFunctionIds: current.memberFunctionIds.includes(functionId)
        ? current.memberFunctionIds.filter((item) => item !== functionId)
        : [...current.memberFunctionIds, functionId],
    }));
  };

  const handleSave = async () => {
    if (!canManage) return;

    const issue = validateToolPluginForm(form, {
      existingKernelPluginNames: plugins.map((plugin) => ({
        pluginId: plugin.pluginId,
        kernelPluginName: plugin.kernelPluginName,
      })),
      catalog,
    });

    if (issue) {
      setFormIssue(issue);
      return;
    }

    setFormIssue(null);
    setSaving(true);
    try {
      const payload = {
        kernelPluginName: form.kernelPluginName.trim(),
        displayName: form.displayName.trim(),
        description: form.description.trim(),
        isPublished: form.isPublished,
        memberFunctionIds: form.memberFunctionIds,
        ...(canEditUseCaseAssignments ? { assignedUseCaseIds: form.assignedUseCaseIds } : {}),
      };

      if (drawerMode === 'edit') {
        await updateAiToolPlugin(form.pluginId, payload);
        showToast(t('pluginUpdated'), 'success');
      } else {
        await createAiToolPlugin({
          ...payload,
          pluginId: form.pluginId.trim() || undefined,
        });
        showToast(t('pluginCreated'), 'success');
      }

      setDrawerMode(null);
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToSavePlugin'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plugin: AiToolPluginDefinition) => {
    if (!canManage) return;
    if (!window.confirm(t('confirmDeletePlugin', { name: plugin.displayName }))) return;

    try {
      await deleteAiToolPlugin(plugin.pluginId);
      showToast(t('pluginDeleted'), 'success');
      if (form.pluginId === plugin.pluginId) {
        setDrawerMode(null);
      }
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToDeletePlugin'), 'error');
    }
  };

  /* ----------------------------------------------------------- fragments */

  const renderEmptyState = () => (
    <div className={styles.empty}>
      <span className={styles.emptyIcon}><LayersIcon size={22} /></span>
      <span className={styles.emptyTitle}>{t('emptyTitle')}</span>
      <p className={styles.emptyText}>{t('emptyText')}</p>
      <span className={styles.emptyExample}>{t('emptyExample')}</span>
      {canManage ? (
        <div className={styles.emptyActions}>
          <button type="button" className="btn btn-primary" onClick={openCreateDrawer}>
            <PlusIcon />
            <span>{t('createPlugin')}</span>
          </button>
        </div>
      ) : null}
    </div>
  );

  const renderPluginCard = (plugin: AiToolPluginDefinition) => (
    <article
      key={plugin.pluginId}
      className={`${styles.pluginCard} ${plugin.isPublished ? styles.pluginCardPublished : ''}`}
    >
      <div className={styles.pluginCardTop}>
        <span className={styles.pluginName}>{plugin.displayName}</span>
        <span className={`${styles.badge} ${plugin.isPublished ? styles.badgeSuccess : styles.badgeWarn}`}>
          {plugin.isPublished ? <CheckIcon size={12} /> : null}
          {plugin.isPublished ? t('published') : t('draft')}
        </span>
      </div>

      <div className={styles.pluginBadges}>
        <span className={`${styles.badge} ${styles.badgeMono}`}>{plugin.kernelPluginName}</span>
        <span className={styles.badge}>
          <ToolboxIcon size={12} />
          {t('memberCount', { count: plugin.memberFunctionIds.length })}
        </span>
      </div>

      {plugin.description ? (
        <p className={styles.pluginText}>{plugin.description}</p>
      ) : null}

      <div className={styles.pluginCardActions}>
        <button
          type="button"
          className={styles.iconButton}
          title={canManage ? t('editPlugin') : t('viewPlugin')}
          aria-label={canManage ? t('editPlugin') : t('viewPlugin')}
          onClick={() => openEditDrawer(plugin)}
        >
          <PencilIcon size={15} />
        </button>
        {canManage ? (
          <button
            type="button"
            className={`${styles.iconButton} ${styles.iconButtonDanger}`}
            title={t('deletePlugin')}
            aria-label={t('deletePlugin')}
            onClick={() => void handleDelete(plugin)}
          >
            <TrashIcon size={15} />
          </button>
        ) : null}
      </div>
    </article>
  );

  const renderToolPicker = () => {
    const mcpCount = sortedCatalog.filter((item) => item.source === 'mcp').length;
    const apiCount = sortedCatalog.filter((item) => item.source === 'api').length;

    return (
      <div>
        <div className={styles.pickerHead}>
          <div>
            <span className={styles.sectionLabel}>{t('membersTitle')}</span>
            <p className={styles.fieldHint}>{t('membersHelp')}</p>
          </div>
          <span className={styles.pickerCount}>
            {t('selectedCount', { count: form.memberFunctionIds.length })}
          </span>
        </div>

        <div className={styles.pickerToolbar}>
          <div className={`${styles.searchField} ${styles.listToolbarSearch}`}>
            <SearchIcon className={styles.searchFieldIcon} size={15} />
            <input
              className={styles.searchInput}
              value={pickerQuery}
              placeholder={t('searchTools')}
              onChange={(event) => setPickerQuery(event.target.value)}
            />
          </div>
          <div className={styles.segmented}>
            {([
              { id: 'all' as SourceFilter, label: t('sourceAll'), count: sortedCatalog.length },
              { id: 'mcp' as SourceFilter, label: t('sourceMcp'), count: mcpCount },
              { id: 'api' as SourceFilter, label: t('sourceApi'), count: apiCount },
            ]).map((option) => (
              <button
                key={option.id}
                type="button"
                className={`${styles.segment} ${sourceFilter === option.id ? styles.segmentActive : ''}`}
                onClick={() => setSourceFilter(option.id)}
              >
                {option.label}
                <span className={styles.segmentCount}>{option.count}</span>
              </button>
            ))}
          </div>
        </div>

        {selectedMembers.length > 0 ? (
          <div className={styles.selectedChips}>
            {selectedMembers.map((member) => (
              <span key={member.functionId} className={`${styles.badge} ${styles.badgeAccent}`}>
                {member.displayName}
                {canManage ? (
                  <button
                    type="button"
                    className={styles.chipRemove}
                    title={t('removeMember')}
                    aria-label={t('removeMember')}
                    disabled={saving}
                    onClick={() => toggleMember(member.functionId)}
                  >
                    <CloseIcon size={11} />
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        ) : null}

        <div className={styles.pickerList}>
          {visibleCatalog.length === 0 ? (
            <div className={styles.inlineEmpty}>
              {sortedCatalog.length === 0 ? t('noToolsAvailable') : t('noSearchResults')}
            </div>
          ) : visibleCatalog.map((item) => {
            const isSelected = form.memberFunctionIds.includes(item.functionId);
            const otherPluginNames = item.memberOfPluginIds
              .filter((pluginId) => pluginId !== form.pluginId)
              .map((pluginId) => pluginNamesById.get(pluginId) ?? pluginId);

            return (
              <label
                key={item.functionId}
                className={`${styles.pickerItem} ${isSelected ? styles.pickerItemSelected : ''}`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={!canManage || saving}
                  onChange={() => toggleMember(item.functionId)}
                />
                <span>
                  <span className={styles.pickerItemName}>{item.displayName}</span>
                  <span className={styles.pickerItemMeta}>{item.kernelFunctionName}</span>
                  <span className={styles.pickerItemBadges}>
                    <span className={`${styles.badge} ${styles.badgeInline}`}>
                      {item.source === 'mcp' ? t('sourceMcp') : t('sourceApi')}
                    </span>
                    {!item.isPublished ? (
                      <span className={`${styles.badge} ${styles.badgeInline} ${styles.badgeWarn}`}>
                        {t('toolUnpublished')}
                      </span>
                    ) : null}
                    {otherPluginNames.length > 0 ? (
                      <span className={`${styles.badge} ${styles.badgeInline} ${styles.badgeAccent}`}>
                        {t('alreadyInPlugin', { names: otherPluginNames.join(', ') })}
                      </span>
                    ) : null}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDrawer = () => {
    if (!drawerMode) {
      return null;
    }

    return (
      <div
        className={styles.drawerOverlay}
        role="presentation"
        onClick={() => {
          if (!saving) {
            setDrawerMode(null);
          }
        }}
      >
        <div
          className={styles.drawer}
          role="dialog"
          aria-modal="true"
          aria-busy={saving}
          aria-label={drawerMode === 'edit' ? t('editPlugin') : t('createPlugin')}
          onClick={(event) => event.stopPropagation()}
        >
          {saving ? (
            <>
              <span className={styles.busyBar} aria-hidden="true" />
              <div className={styles.busyOverlay} role="status" aria-live="polite">
                <Spinner size={36} />
                <div className={styles.busyCopy}>
                  <strong>{t('savingPlugin')}</strong>
                  <span>{t('savingPluginHelp')}</span>
                </div>
              </div>
            </>
          ) : null}
          <div className={styles.drawerHead}>
            <div>
              <h3 className={styles.drawerTitle}>
                {drawerMode === 'edit' ? t('editPlugin') : t('createPlugin')}
              </h3>
              <p className={styles.drawerSubtitle}>{t('formHelp')}</p>
            </div>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={t('close', { ns: 'common' })}
              disabled={saving}
              onClick={() => setDrawerMode(null)}
            >
              <CloseIcon size={15} />
            </button>
          </div>

          <div className={styles.drawerBody}>
            {formIssue ? (
              <div className={styles.formError}>
                <span className={styles.formErrorIcon}><AlertIcon size={15} /></span>
                <span>{t(`issue_${formIssue}`)}</span>
              </div>
            ) : null}

            <div className={styles.formGrid}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('displayName')}</span>
                <input
                  className="text-input"
                  value={form.displayName}
                  placeholder={t('displayNamePlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => handleDisplayNameChange(event.target.value)}
                />
              </label>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('kernelPluginName')}</span>
                <input
                  className={`text-input ${styles.monoInput}`}
                  value={form.kernelPluginName}
                  placeholder={t('kernelPluginNamePlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => {
                    setKernelNameTouched(true);
                    setForm((current) => ({ ...current, kernelPluginName: event.target.value }));
                  }}
                />
                <span className={styles.fieldHint}>{t('kernelPluginNameHint')}</span>
              </label>

              <label className={`${styles.field} ${styles.fieldWide}`}>
                <span className={styles.fieldLabel}>{t('description')}</span>
                <textarea
                  className="text-input"
                  rows={3}
                  value={form.description}
                  placeholder={t('descriptionPlaceholder')}
                  disabled={!canManage || saving}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                />
              </label>
            </div>

            <label className={`${styles.checkRow} ${form.isPublished ? styles.checkRowPublished : ''}`}>
              <input
                type="checkbox"
                checked={form.isPublished}
                disabled={!canManage || saving}
                onChange={(event) => setForm((current) => ({ ...current, isPublished: event.target.checked }))}
              />
              <span>
                <span className={styles.checkRowLabel}>{t('publishPlugin')}</span>
                <span className={styles.checkRowHint}>{t('publishPluginHelp')}</span>
              </span>
            </label>

            <div className={styles.useCaseBlock}>
              <button
                type="button"
                className={styles.disclosure}
                aria-expanded={showUseCaseOptions}
                onClick={() => setShowUseCaseOptions((current) => !current)}
              >
                <span>{t('useCaseOptions')}</span>
                <ChevronDownIcon
                  size={14}
                  className={`${styles.disclosureIcon} ${showUseCaseOptions ? styles.disclosureIconOpen : ''}`}
                />
              </button>

              {showUseCaseOptions ? (
                <>
                  <ToolUseCaseAssignment
                    selectedUseCaseIds={form.assignedUseCaseIds}
                    disabled={!canManage || saving || !canEditUseCaseAssignments}
                    isPublished={form.isPublished}
                    onChange={(assignedUseCaseIds) => setForm((current) => ({
                      ...current,
                      assignedUseCaseIds,
                    }))}
                  />
                  <span className={styles.fieldHint}>{t('useCaseOptionsHint')}</span>
                </>
              ) : null}
            </div>

            {renderToolPicker()}
          </div>

          <div className={styles.drawerFoot}>
            {canManage ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving}
                onClick={() => void handleSave()}
              >
                {saving ? <span className={styles.btnSpinner} aria-hidden="true" /> : null}
                <span>{saving ? t('savingPlugin') : t('savePlugin')}</span>
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-secondary"
              disabled={saving}
              onClick={() => setDrawerMode(null)}
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* -------------------------------------------------------------- render */

  if (loading) {
    return (
      <div className={styles.pageLoading}>
        <Spinner />
        <span>{t('loading', { ns: 'common' })}</span>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageBar}>
        <div className={styles.statStrip}>
          <span className={styles.stat}>
            <LayersIcon className={styles.statIcon} size={15} />
            <span className={styles.statValue}>{plugins.length}</span>
            {t('statPlugins')}
          </span>
          <span className={styles.stat}>
            <CheckIcon className={styles.statIcon} size={15} />
            <span className={styles.statValue}>{publishedPluginCount}</span>
            {t('statPublished')}
          </span>
          <span className={styles.stat}>
            <ToolboxIcon className={styles.statIcon} size={15} />
            <span className={styles.statValue}>{groupedToolCount}</span>
            {t('statGroupedTools')}
          </span>
        </div>

        {canManage ? (
          <div className={styles.pageBarActions}>
            <button type="button" className="btn btn-primary" onClick={openCreateDrawer}>
              <PlusIcon />
              <span>{t('createPlugin')}</span>
            </button>
          </div>
        ) : null}
      </div>

      <header className={styles.hero}>
        <span className={styles.heroMark}><LayersIcon size={22} /></span>
        <div className={styles.heroBody}>
          <h3 className={styles.heroTitle}>{t('title')}</h3>
          <p className={styles.heroText}>{t('subtitle')}</p>
        </div>
      </header>

      <section className={styles.card}>
        <div className={styles.cardHead}>
          <div>
            <h3 className={styles.cardTitle}>{t('pluginListTitle')}</h3>
            <p className={styles.cardIntro}>{t('pluginListHelp')}</p>
          </div>
        </div>

        {plugins.length === 0 ? renderEmptyState() : (
          <>
            <div className={styles.listToolbar}>
              <div className={`${styles.searchField} ${styles.listToolbarSearch}`}>
                <SearchIcon className={styles.searchFieldIcon} size={15} />
                <input
                  className={styles.searchInput}
                  value={query}
                  placeholder={t('searchPlugins')}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              <div className={styles.segmented}>
                {([
                  { id: 'all' as StatusFilter, label: t('filterAll'), count: plugins.length },
                  { id: 'published' as StatusFilter, label: t('filterPublished'), count: publishedPluginCount },
                  {
                    id: 'draft' as StatusFilter,
                    label: t('filterDraft'),
                    count: plugins.length - publishedPluginCount,
                  },
                ]).map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`${styles.segment} ${statusFilter === option.id ? styles.segmentActive : ''}`}
                    onClick={() => setStatusFilter(option.id)}
                  >
                    {option.label}
                    <span className={styles.segmentCount}>{option.count}</span>
                  </button>
                ))}
              </div>
            </div>

            {visiblePlugins.length === 0 ? (
              <div className={styles.inlineEmpty}>{t('noSearchResults')}</div>
            ) : (
              <div className={styles.pluginGrid}>
                {visiblePlugins.map(renderPluginCard)}
              </div>
            )}
          </>
        )}
      </section>

      {renderDrawer()}
    </div>
  );
};

export default ToolPluginsPage;
